'use client'

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  /** Unique section identifier for tracking */
  id: string
  /** Human-readable label shown in analytics */
  label?: string
  /** Section order (auto-detected if not provided) */
  order?: number
  /** Full height section (100vh) */
  fullHeight?: boolean
  /** Additional className */
  className?: string
  /** Section content */
  children?: ReactNode
}

/**
 * Trackable Section Component
 *
 * A section that automatically integrates with the Brixon tracking system.
 * The IntersectionObserver in the tracking SDK observes all elements with
 * data-section-id attributes.
 *
 * @example
 * ```tsx
 * <Section id="hero" label="Introduction">
 *   <h1>Welcome</h1>
 * </Section>
 *
 * <Section id="features" label="Features" fullHeight>
 *   <FeatureList />
 * </Section>
 * ```
 */
export const Section = forwardRef<HTMLElement, SectionProps>(
  ({ id, label, order, fullHeight = false, className, children, ...props }, ref) => {
    return (
      <section
        ref={ref}
        id={id}
        data-section-id={id}
        data-section-label={label || id}
        data-section-order={order}
        className={cn(
          'relative',
          fullHeight && 'min-h-screen',
          className
        )}
        {...props}
      >
        {children}
      </section>
    )
  }
)

Section.displayName = 'Section'
